import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Table } from "react-bootstrap";
import { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import { useAuth } from "../../../app/providers/AuthProvider";
import { usePlanLimits } from "../../academy/hooks/usePlanLimits";
import { useOwnerPayments } from "../../billing/hooks/useOwnerPayments";
import { usePortfolio } from "../../billing/hooks/usePortfolio";
import { useSaasSubscription } from "../../plan/hooks/useSaasSubscription";
import { StatCard, QuickAction, SectionTitle, DashboardGreeting } from "../components/DashboardKit";
import { RegistrationQrModal } from "../components/RegistrationQrModal";

const COP = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 0,
});

function OwnerDashboard() {
  const { slug } = useParams();
  const { user } = useAuth();
  const name = user?.name || user?.email || "Propietario";
  const base = `/${slug}/app`;

  const { data: limits } = usePlanLimits();
  const { data: payments } = useOwnerPayments();
  const { data: overdue } = usePortfolio();
  const { data: subscription } = useSaasSubscription();

  // Enlace público de inscripción de la academia — para que el propietario lo
  // comparta con estudiantes nuevos (se registran solos, sin que el owner
  // tenga que crear su cuenta manualmente). window.location.origin en vez de
  // una env var: así funciona igual en local/staging/producción sin config.
  const registrationUrl = `${window.location.origin}/${slug}/registro`;
  const [showQrModal, setShowQrModal] = useState(false);

  // Oculta accesos a features no incluidas en el plan (honorarios). Mientras
  // carga (undefined) se muestra; solo se oculta si el plan lo tiene en false.
  const showHonorarios = subscription?.plan?.features?.has_honorarios ?? true;

  const revenue = (payments ?? []).reduce(
    (sum, p) => sum + Number(p.amount || 0),
    0
  );
  const usage = (l) => (l ? `${l.used}${l.limit != null ? ` / ${l.limit}` : ""}` : "—");
  const recent = (payments ?? []).slice(0, 5);

  // Subtítulo del card de estudiantes: prioriza "en espera de cupo" (accionable);
  // si no, muestra cuántos tienen un plan de estudio activo.
  const st = limits?.students;
  let studentsSub;
  if (st?.frozen > 0) {
    studentsSub = `${st.frozen} en espera de cupo`;
  } else if (st?.studying != null) {
    studentsSub = `${st.studying} con plan de estudio`;
  }

  return (
    <div className="container-fluid px-0 py-3">
      <Toaster position="top-right" />
      <DashboardGreeting
        name={name}
        subtitle="Panel de tu academia — un vistazo a tu operación de hoy."
        actions={
          <button
            type="button"
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
            onClick={() => setShowQrModal(true)}
          >
            <i className="mdi mdi-share-variant" aria-hidden="true" />
            Compartir enlace de inscripción
          </button>
        }
      />

      <RegistrationQrModal
        show={showQrModal}
        onHide={() => setShowQrModal(false)}
        slug={slug}
        registrationUrl={registrationUrl}
      />

      <div className="row g-3">
        <div className="col-6 col-lg-3">
          <StatCard
            icon="mdi-account-school"
            variant="primary"
            label="Estudiantes activos"
            value={usage(limits?.students)}
            sub={studentsSub}
          />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard
            icon="mdi-account-tie"
            variant="info"
            label="Docentes"
            value={usage(limits?.teachers)}
            to={`${base}/academia/docentes`}
          />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard
            icon="mdi-cash-multiple"
            variant="success"
            label="Ingresos registrados"
            value={COP.format(revenue)}
            sub={`${(payments ?? []).length} pago(s)`}
            to={`${base}/academia/dinero/pagos`}
          />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard
            icon="mdi-briefcase-outline"
            variant={(overdue ?? []).length > 0 ? "danger" : "success"}
            label="Cartera vencida"
            value={(overdue ?? []).length}
            to={`${base}/academia/dinero/cartera`}
          />
        </div>
      </div>

      <SectionTitle>Accesos rápidos</SectionTitle>
      <div className="row g-2">
        <div className="col-6 col-md-4 col-lg-2">
          <QuickAction to={`${base}/academia/dinero/pagos`} icon="mdi-cash-register" label="Registrar pago" variant="primary" />
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <QuickAction to={`${base}/academia/docentes`} icon="mdi-account-multiple-plus" label="Docentes" />
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <QuickAction to={`${base}/academia/temas`} icon="mdi-tag-multiple" label="Temas" />
        </div>
        <div className="col-6 col-md-4 col-lg-2">
          <QuickAction to={`${base}/academia/paquetes`} icon="mdi-package-variant-closed" label="Paquetes" />
        </div>
        {showHonorarios && (
          <div className="col-6 col-md-4 col-lg-2">
            <QuickAction to={`${base}/academia/dinero/honorarios`} icon="mdi-cash" label="Honorarios" />
          </div>
        )}
        <div className="col-6 col-md-4 col-lg-2">
          <QuickAction to={`${base}/academia/sedes`} icon="mdi-map-marker-outline" label="Sedes" />
        </div>
      </div>

      <SectionTitle>Pagos recientes</SectionTitle>
      {recent.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center text-muted py-4">
            Aún no hay pagos registrados.
          </Card.Body>
        </Card>
      ) : (
        <>
          {/* PC: tabla */}
          <Card className="border-0 shadow-sm d-none d-md-block">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Estudiante</th>
                    <th className="text-end">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((p) => (
                    <tr key={p.id}>
                      <td>{dayjs(p.date).format("D MMM YYYY")}</td>
                      <td>{p.student?.full_name ?? p.student?.email ?? "—"}</td>
                      <td className="text-end fw-medium">{COP.format(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>

          {/* Móvil: tarjetas */}
          <div className="d-md-none d-flex flex-column gap-2">
            {recent.map((p) => (
              <Card key={p.id} className="border-0 shadow-sm">
                <Card.Body className="d-flex justify-content-between align-items-center gap-3 py-2 px-3">
                  <div style={{ minWidth: 0 }}>
                    <div className="fw-medium text-truncate">
                      {p.student?.full_name ?? p.student?.email ?? "—"}
                    </div>
                    <div className="text-muted small">
                      {dayjs(p.date).format("D MMM YYYY")}
                    </div>
                  </div>
                  <div className="fw-bold text-nowrap">{COP.format(p.amount)}</div>
                </Card.Body>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export { OwnerDashboard };
